import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  policyService,
  Policy,
  PolicyMode,
  PolicySeverity,
  CreatePolicyParams,
  DryRunResult,
  PolicyViolation,
} from '../services/policyService';
import { Button } from '../components/common/Button';
import { IconButton } from '../components/common/IconButton';
import { Badge, BadgeVariant } from '../components/common/Badge';

/**
 * Policy Editor Page
 * Requirement 47 [MVP]: Policy and Governance Engine (backend evaluation,
 * dry-run, violation tracking) + Requirement 80 [V01]: this editor UI.
 *
 * Preventive/corrective enforcement (actually blocking a resource from being
 * created, or auto-stopping one) needs a real provisioning pipeline and the
 * Auto-Remediation Engine (Requirement 49 [V01]) - neither exists yet, so
 * those modes can be authored and dry-run here but only 'detective' mode
 * actually tracks violations against the resource catalog.
 */

const MODE_LABELS: Record<PolicyMode, { label: string; variant: BadgeVariant }> = {
  preventive: { label: 'Preventive', variant: 'danger' },
  detective: { label: 'Detective', variant: 'info' },
  corrective: { label: 'Corrective', variant: 'default' },
};

const SEVERITY_VARIANTS: Record<PolicySeverity, BadgeVariant> = {
  low: 'muted',
  medium: 'warning',
  high: 'warning',
  critical: 'danger',
};

export const PolicyEditorPage: React.FC = () => {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [violationCounts, setViolationCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadPolicies = async () => {
    setLoading(true);
    setError(null);
    try {
      const [fetchedPolicies, violations] = await Promise.all([
        policyService.listPolicies(),
        policyService.listViolations('open'),
      ]);
      setPolicies(fetchedPolicies);
      const counts: Record<string, number> = {};
      violations.forEach((v: PolicyViolation) => {
        counts[v.policy_id] = (counts[v.policy_id] || 0) + 1;
      });
      setViolationCounts(counts);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, []);

  const handleToggle = async (policy: Policy) => {
    setBusyId(policy.id);
    try {
      await policyService.updatePolicy(policy.id, { enabled: !policy.enabled });
      await loadPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update policy');
    } finally {
      setBusyId(null);
    }
  };

  const handleDryRun = async (policy: Policy) => {
    setBusyId(policy.id);
    setError(null);
    try {
      const result = await policyService.dryRunPolicy(policy.id);
      setDryRunResult(result);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Dry run failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleEvaluate = async (policy: Policy) => {
    setBusyId(policy.id);
    setError(null);
    try {
      const result = await policyService.evaluatePolicy(policy.id);
      setDryRunResult(result);
      await loadPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Evaluation failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleSave = async (params: CreatePolicyParams, id?: string) => {
    setError(null);
    try {
      if (id) {
        await policyService.updatePolicy(id, params);
      } else {
        await policyService.createPolicy(params);
      }
      setEditingPolicy(null);
      setIsCreating(false);
      await loadPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save policy');
    }
  };

  const handleDelete = async (id: string) => {
    setBusyId(id);
    try {
      await policyService.deletePolicy(id);
      await loadPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete policy');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button onClick={() => setIsCreating(true)} size="sm">
          <Plus /> New Policy
        </Button>
      </div>

      {error && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-rose-300 bg-rose-50 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-200">
          <span>{error}</span>
          <IconButton onClick={() => setError(null)} aria-label="Dismiss">
            <X />
          </IconButton>
        </div>
      )}

      {dryRunResult && (
        <div className="rounded-md border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary dark:border-primary/30 dark:bg-primary/10 dark:text-primary">
          <div className="flex items-start justify-between gap-3">
            <span className="font-medium">
              "{dryRunResult.policy_name}" matched {dryRunResult.matched_count} resource
              {dryRunResult.matched_count === 1 ? '' : 's'}
              {'new_violations' in dryRunResult && (
                <>
                  {' '}— {(dryRunResult as any).new_violations} new violation
                  {(dryRunResult as any).new_violations === 1 ? '' : 's'} opened,{' '}
                  {(dryRunResult as any).resolved_violations} resolved
                </>
              )}
            </span>
            <IconButton onClick={() => setDryRunResult(null)} aria-label="Dismiss">
              <X />
            </IconButton>
          </div>
          {dryRunResult.matches.length > 0 && (
            <div className="mt-2 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-primary">
                    <th className="pr-4 py-1">Resource</th>
                    <th className="pr-4 py-1">Provider</th>
                    <th className="pr-4 py-1">Service</th>
                    <th className="pr-4 py-1">Region</th>
                    <th className="pr-4 py-1">Monthly Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {dryRunResult.matches.map((m) => (
                    <tr key={m.resource_id} className="border-t border-primary/30">
                      <td className="pr-4 py-1 font-mono">{m.resource_id}</td>
                      <td className="pr-4 py-1 uppercase">{m.provider}</td>
                      <td className="pr-4 py-1">{m.service}</td>
                      <td className="pr-4 py-1">{m.region}</td>
                      <td className="pr-4 py-1">${m.monthly_cost.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground dark:border-border dark:text-muted-foreground/70">
          Loading policies...
        </div>
      ) : (
        <div className="space-y-3">
          {policies.map((policy) => (
            <div
              key={policy.id}
              className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">{policy.name}</h3>
                    <Badge variant={MODE_LABELS[policy.mode].variant}>{MODE_LABELS[policy.mode].label}</Badge>
                    <Badge variant={SEVERITY_VARIANTS[policy.severity]}>{policy.severity.toUpperCase()}</Badge>
                    {policy.enabled && <Badge variant="success">ENABLED</Badge>}
                    {violationCounts[policy.id] > 0 && (
                      <Badge variant="danger">
                        {violationCounts[policy.id]} open violation{violationCounts[policy.id] === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{policy.description}</p>
                </div>

                <div className="flex flex-none items-center gap-1.5">
                  <button
                    onClick={() => handleDryRun(policy)}
                    disabled={busyId === policy.id}
                    className="rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-50 dark:text-primary dark:hover:bg-primary/10"
                  >
                    Dry Run
                  </button>
                  <button
                    onClick={() => handleEvaluate(policy)}
                    disabled={busyId === policy.id}
                    className="rounded px-2 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-50 dark:text-sky-300 dark:hover:bg-sky-900/30"
                  >
                    Evaluate
                  </button>
                  <button
                    onClick={() => setEditingPolicy(policy)}
                    className="rounded px-2 py-1 text-xs font-medium text-foreground/80 hover:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
                  >
                    Edit
                  </button>
                  <label className="relative ml-1 inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={policy.enabled}
                      onChange={() => handleToggle(policy)}
                      disabled={busyId === policy.id}
                      className="peer sr-only"
                    />
                    <div className="peer h-6 w-11 rounded-full bg-secondary after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-border after:bg-card after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring dark:border-border dark:bg-secondary dark:peer-focus:ring-ring"></div>
                  </label>
                  <button
                    onClick={() => handleDelete(policy.id)}
                    disabled={busyId === policy.id}
                    className="rounded px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:text-rose-400 dark:hover:bg-rose-900/20"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <details className="mt-3">
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground/80 dark:text-muted-foreground/70 dark:hover:text-foreground">
                  View policy definition
                </summary>
                <pre className="mt-2 overflow-x-auto rounded bg-gray-900 p-3 text-xs text-muted-foreground/70">
                  {policy.conditions}
                </pre>
              </details>
            </div>
          ))}

          {policies.length === 0 && (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground dark:border-border dark:text-muted-foreground/70">
              No policies yet. Click "New Policy" to create one.
            </div>
          )}
        </div>
      )}

      {(editingPolicy || isCreating) && (
        <PolicyFormModal
          policy={editingPolicy}
          onSave={handleSave}
          onCancel={() => {
            setEditingPolicy(null);
            setIsCreating(false);
          }}
        />
      )}
    </div>
  );
};

const PolicyFormModal: React.FC<{
  policy: Policy | null;
  onSave: (params: CreatePolicyParams, id?: string) => void;
  onCancel: () => void;
}> = ({ policy, onSave, onCancel }) => {
  const [name, setName] = useState(policy?.name ?? '');
  const [description, setDescription] = useState(policy?.description ?? '');
  const [mode, setMode] = useState<PolicyMode>(policy?.mode ?? 'detective');
  const [severity, setSeverity] = useState<PolicySeverity>(policy?.severity ?? 'medium');
  const [conditions, setConditions] = useState(
    policy?.conditions ?? 'conditions:\n  - metric: cpu_utilization\n    operator: less_than\n    value: 5\naction:\n  type: alert'
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(
      {
        name,
        description,
        mode,
        severity,
        enabled: policy?.enabled ?? false,
        conditions,
      },
      policy?.id
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-card p-8 shadow-2xl dark:bg-card"
      >
        <h2 className="text-xl font-semibold text-foreground">
          {policy ? 'Edit Policy' : 'New Policy'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Define a cost-governance rule as policy-as-code.
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80">Name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-border px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80">Description</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 block w-full rounded-md border border-border px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground/80">Mode</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as PolicyMode)}
                className="mt-1 block w-full rounded-md border border-border px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
              >
                <option value="preventive">Preventive</option>
                <option value="detective">Detective</option>
                <option value="corrective">Corrective</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/80">Severity</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as PolicySeverity)}
                className="mt-1 block w-full rounded-md border border-border px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80">
              Conditions (YAML)
            </label>
            <textarea
              rows={8}
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              spellCheck={false}
              className="mt-1 block w-full rounded-md border border-border bg-muted/30 px-3 py-2.5 font-mono text-sm leading-relaxed shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-card dark:text-foreground"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Supports metric conditions (cpu_utilization / memory_utilization / cpu_p95 / memory_p95
              with less_than / greater_than / equals) and tag conditions (equals / exists).
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Save Policy
          </button>
        </div>
      </form>
    </div>
  );
};
